import React, {useEffect, useRef, useState} from 'react';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {CalendarDaysIcon, ClockIcon} from '@heroicons/react/24/outline';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import DatePickerField from '@/components/ui/DatePickerField';
import {SkeletonCard} from '@/components/ui/LoadingSkeleton';
import NativePickerInput from '@/components/ui/NativePickerInput';
import {operationsAPI} from '@/features/operations/api';
import {operationsJobsQuery, runNowMutation, updateJobMutation} from '@/features/operations/queries';
import type {GuidedSchedule, OperationsError, ScheduledJob, Slot, WorkerView} from '@/features/operations/types';

const weekdays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

type EditState = {
  job: ScheduledJob;
  schedule: GuidedSchedule;
  enabled: boolean;
  version: number;
};

const describeSchedule = (schedule: GuidedSchedule): string => {
  switch (schedule.kind) {
    case 'interval':
      return `Every ${schedule.intervalMinutes} minutes`;
    case 'daily':
      return `Daily at ${schedule.time}`;
    case 'weekly':
      return `${weekdays[schedule.weekday - 1]} at ${schedule.time}`;
  }
};

const resultText = (job: ScheduledJob): string => {
  const summary = job.lastRun?.resultSummary;
  if (!summary) return 'No completed run yet';
  return `${summary.succeeded} succeeded, ${summary.failed} failed, ${summary.skipped} skipped`;
};

const hasActiveRun = (job: ScheduledJob): boolean => job.activeRun?.state === 'queued' || job.activeRun?.state === 'running' || job.activeRun?.state === 'cancel_requested';

const errorFrom = (error: unknown): OperationsError | undefined => (
  error as {response?: {data?: {error?: OperationsError}}}
).response?.data?.error;

const londonToday = (now = new Date()): string => {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now);
  const value = (type: Intl.DateTimeFormatPartTypes) => parts.find(part => part.type === type)?.value ?? '';
  return `${value('year')}-${value('month')}-${value('day')}`;
};

const isCanonicalDate = (value: string): boolean => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [year, month, day] = value.split('-').map(Number);
  const parsed = new Date(Date.UTC(year, month - 1, day));
  return parsed.getUTCFullYear() === year && parsed.getUTCMonth() === month - 1 && parsed.getUTCDate() === day;
};

const validateStartDate = (job: ScheduledJob, schedule: GuidedSchedule): string => {
  if (schedule.startDate && schedule.startDate !== job.schedule.startDate) {
    if (!isCanonicalDate(schedule.startDate)) return 'Enter a valid start date.';
    if (schedule.startDate < londonToday()) return 'Choose today or a future date.';
  }
  return '';
};

const validateSchedule = (job: ScheduledJob, schedule: GuidedSchedule): string => {
  const startDateError = validateStartDate(job, schedule);
  if (startDateError) return startDateError;
  if (schedule.kind === 'interval') {
    const minimum = job.minimumMinutes ?? Math.min(...(job.allowedIntervals ?? [1]));
    if (!Number.isFinite(schedule.intervalMinutes) || schedule.intervalMinutes < minimum) return `Use at least ${minimum} minutes.`;
  }
  if ((schedule.kind === 'daily' || schedule.kind === 'weekly') && !/^\d{2}:\d{2}$/.test(schedule.time)) return 'Enter a valid local time.';
  return '';
};

const scheduleWithKind = (kind: GuidedSchedule['kind'], previous: GuidedSchedule): GuidedSchedule => {
  if (kind === previous.kind) return previous;
  const start = previous.startDate ? {startDate: previous.startDate} : {};
  if (kind === 'interval') return {kind, intervalMinutes: 30, ...start};
  if (kind === 'daily') return {kind, time: '06:00', ...start};
  return {kind, weekday: 1, time: '09:00', ...start};
};

const StatusBadge: React.FC<{children: React.ReactNode; tone: 'green' | 'amber' | 'red' | 'blue' | 'gray'}> = ({children, tone}) => {
  const tones = {
    green: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
    amber: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
    red: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
    blue: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
    gray: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
  } as const;
  return <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${tones[tone]}`}>{children}</span>;
};

interface ConfirmDialogProps {
  isOpen: boolean;
  title: string;
  message: React.ReactNode;
  confirmLabel: string;
  loading?: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({isOpen, title, message, confirmLabel, loading = false, onClose, onConfirm}) => {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const titleId = title.toLowerCase().replace(/[^a-z0-9]+/g, '-');

  useEffect(() => {
    if (!isOpen) return undefined;
    cancelRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !loading) onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, loading, onClose]);

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-labelledby={titleId}>
      <div className="fixed inset-0 bg-black/50" />
      <div className="fixed inset-0 w-screen overflow-y-auto p-4">
        <div className="flex min-h-full items-center justify-center">
          <div className="my-8 min-w-0 w-full max-w-md rounded-lg bg-white p-6 shadow-xl dark:bg-gray-800">
            <h2 id={titleId} className="mb-2 break-words text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</h2>
            <div className="mb-5 break-words text-sm text-gray-600 dark:text-gray-400">{message}</div>
            <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:justify-end">
              <button
                ref={cancelRef}
                type="button"
                onClick={onClose}
                disabled={loading}
                className="min-h-11 w-full whitespace-normal rounded-md bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200 disabled:opacity-50 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 sm:w-auto"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={onConfirm}
                disabled={loading}
                className="min-h-11 w-full whitespace-normal rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
              >
                {loading ? 'Working...' : confirmLabel}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const WorkerCard: React.FC<{worker: WorkerView}> = ({worker}) => (
  <article aria-label={`${worker.label} worker card`} className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-900">
    <div className="flex items-start justify-between gap-3">
      <div>
        <h3 className="font-semibold text-gray-900 dark:text-gray-100">{worker.label}</h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Read-only worker status and runtime configuration.</p>
      </div>
      <StatusBadge tone={worker.available ? worker.running ? 'green' : 'blue' : 'red'}>{worker.available ? worker.running ? 'Running' : 'Ready' : 'Unavailable'}</StatusBadge>
    </div>
    {worker.config && (
      <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
        {Object.entries(worker.config).map(([key, value]) => (
          <div key={key}>
            <dt className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{key}</dt>
            <dd className="mt-1 break-words text-gray-800 dark:text-gray-200">{String(value)}</dd>
          </div>
        ))}
      </dl>
    )}
  </article>
);

interface EditDialogProps {
  state: EditState | null;
  saving: boolean;
  onClose: () => void;
  onSave: (state: EditState) => Promise<void>;
}

const EditDialog: React.FC<EditDialogProps> = ({state, saving, onClose, onSave}) => {
  const [draft, setDraft] = useState<EditState | null>(state);
  const [preview, setPreview] = useState<Slot[]>([]);
  const [previewError, setPreviewError] = useState('');
  const [stale, setStale] = useState<OperationsError | null>(null);
  const [confirming, setConfirming] = useState(false);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const saveRef = useRef<HTMLButtonElement>(null);

  const restoreSaveFocus = () => {
    saveRef.current?.focus();
    queueMicrotask(() => saveRef.current?.focus());
    window.setTimeout(() => saveRef.current?.focus(), 0);
  };

  useEffect(() => {
    setDraft(state);
    setPreview(state?.job.nextPreview ?? []);
    setPreviewError('');
    setStale(null);
    setConfirming(false);
  }, [state]);

  useEffect(() => {
    if (!draft) return undefined;
    cancelRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !saving) onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [draft, onClose, saving]);

  useEffect(() => {
    if (!draft) return undefined;
    const error = validateSchedule(draft.job, draft.schedule);
    if (error) return undefined;
    const timeout = window.setTimeout(() => {
      operationsAPI.previewSchedule(draft.job.key, draft.schedule)
        .then(result => {
          setPreview(result.slots.slice(0, 3));
          setPreviewError('');
        })
        .catch(() => setPreviewError('Preview unavailable.'));
    }, 300);
    return () => window.clearTimeout(timeout);
  }, [draft]);

  if (!draft) return null;

  const startDateError = validateStartDate(draft.job, draft.schedule);
  const fieldError = validateSchedule(draft.job, draft.schedule);
  const save = async () => {
    if (fieldError) return;
    setConfirming(false);
    restoreSaveFocus();
    try {
      await onSave(draft);
      onClose();
    } catch (error) {
      const operationsError = errorFrom(error);
      if (operationsError?.code === 'stale_version' && operationsError.current) {
        setStale(operationsError);
      } else {
        setPreviewError(operationsError?.message ?? 'Schedule could not be saved.');
      }
    }
  };

  const updateSchedule = (schedule: GuidedSchedule) => {
    setDraft(current => current ? {...current, schedule} : current);
    setStale(null);
  };

  const reloadCurrent = () => {
    if (!stale?.current) return;
    setDraft({job: stale.current, schedule: stale.current.schedule, enabled: stale.current.enabled, version: stale.current.version});
    setPreview(stale.current.nextPreview ?? []);
    setStale(null);
  };

  const closeBroadConfirm = () => {
    setConfirming(false);
    restoreSaveFocus();
  };

  return (
    <>
      <div className="relative z-50" role="dialog" aria-modal="true" aria-labelledby="edit-schedule-title">
        <div className="fixed inset-0 bg-black/50" />
        <div className="fixed inset-0 w-screen overflow-y-auto p-4">
          <div className="flex min-h-full items-center justify-center">
            <div className="my-8 w-full max-w-2xl rounded-xl bg-white p-6 shadow-xl dark:bg-gray-800">
              <h2 id="edit-schedule-title" className="text-lg font-semibold text-gray-900 dark:text-gray-100">Edit {draft.job.label} schedule</h2>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">Guided scheduling uses Europe/London local time. Missed windows are not caught up automatically.</p>

              <div className="mt-5 space-y-5">
                <section aria-labelledby="recurrence-heading">
                  <h3 id="recurrence-heading" className="text-sm font-semibold text-gray-950 dark:text-white">Recurrence</h3>
                  <div className="mt-3 grid gap-4 sm:grid-cols-2">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Schedule type
                      <select
                        value={draft.schedule.kind}
                        onChange={event => updateSchedule(scheduleWithKind(event.target.value as GuidedSchedule['kind'], draft.schedule))}
                        className="mt-1 block min-h-11 w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                      >
                        {(draft.job.allowedKinds ?? ['interval', 'daily', 'weekly']).map(kind => (
                          <option key={kind} value={kind}>{kind === 'interval' ? 'Every interval' : kind === 'daily' ? 'Daily' : 'Weekly'}</option>
                        ))}
                      </select>
                    </label>

                    {draft.schedule.kind === 'interval' && (
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Interval minutes
                        <input
                          aria-label="Interval minutes"
                          type="number"
                          min={draft.job.minimumMinutes ?? Math.min(...(draft.job.allowedIntervals ?? [1]))}
                          value={draft.schedule.intervalMinutes}
                          onChange={event => updateSchedule({kind: 'interval', intervalMinutes: Number(event.target.value), startDate: draft.schedule.startDate})}
                          className="mt-1 block min-h-11 w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                        />
                        <span className="mt-1 block text-xs text-gray-500 dark:text-gray-400">Runs every selected number of minutes.</span>
                      </label>
                    )}

                    {draft.schedule.kind === 'weekly' && (
                      <fieldset className="sm:col-span-2">
                        <legend className="text-sm font-medium text-gray-700 dark:text-gray-300">Weekday</legend>
                        <div className="mt-1 grid grid-cols-4 gap-2 sm:grid-cols-7">
                          {weekdays.map((day, index) => {
                            const weekday = index + 1;
                            const selected = draft.schedule.kind === 'weekly' && draft.schedule.weekday === weekday;
                            return (
                              <button
                                key={day}
                                type="button"
                                aria-label={day}
                                aria-pressed={selected}
                                onClick={() => {
                                  if (draft.schedule.kind === 'weekly') updateSchedule({...draft.schedule, weekday});
                                }}
                                className={`min-h-11 rounded-md border px-2 py-2 text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-800 ${selected ? 'border-primary-600 bg-primary-600 text-white' : 'border-gray-300 bg-white text-gray-700 hover:border-primary-400 hover:text-primary-700 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200 dark:hover:border-primary-400 dark:hover:text-primary-300'}`}
                              >
                                {day.slice(0, 3)}
                              </button>
                            );
                          })}
                        </div>
                      </fieldset>
                    )}
                  </div>
                </section>

                <section aria-labelledby="timing-heading">
                  <h3 id="timing-heading" className="text-sm font-semibold text-gray-950 dark:text-white">Timing</h3>
                  <div className="mt-3 grid gap-4 sm:grid-cols-2">
                    <DatePickerField
                      label="Starts on"
                      value={draft.schedule.startDate ?? ''}
                      min={londonToday()}
                      placeholder="Immediately"
                      clearLabel="Start immediately"
                      aria-invalid={startDateError ? 'true' : undefined}
                      aria-describedby={startDateError ? 'schedule-start-date-error' : undefined}
                      onChange={startDate => updateSchedule({...draft.schedule, startDate: startDate || undefined})}
                    />

                    {(draft.schedule.kind === 'daily' || draft.schedule.kind === 'weekly') && (
                      <div>
                        <label htmlFor="schedule-local-time" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Local time</label>
                        <NativePickerInput
                          id="schedule-local-time"
                          type="time"
                          value={draft.schedule.time}
                          pickerLabel="Choose schedule time"
                          onChange={event => {
                            const current = draft.schedule;
                            if (current.kind === 'daily') updateSchedule({...current, time: event.target.value});
                            if (current.kind === 'weekly') updateSchedule({...current, time: event.target.value});
                          }}
                          className="mt-1 block min-h-11 w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                        />
                      </div>
                    )}
                  </div>
                </section>
              </div>

              {fieldError && <div id={startDateError ? 'schedule-start-date-error' : undefined} role="alert" className="mt-3 rounded-md bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300">{fieldError}</div>}
              {previewError && <div role="alert" className="mt-3 rounded-md bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-900/20 dark:text-amber-300">{previewError}</div>}
              {stale && (
                <div role="alert" className="mt-3 rounded-md bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
                  <p>{stale.message}</p>
                  <p className="mt-1">Current value: {stale.current ? describeSchedule(stale.current.schedule) : 'Unavailable'}</p>
                  <Button type="button" size="sm" variant="outline" className="mt-3" onClick={reloadCurrent}>Reload current schedule values</Button>
                </div>
              )}

              <section className="mt-5 rounded-xl border border-gray-200 bg-gray-50/70 p-4 dark:border-gray-700 dark:bg-gray-900/60">
                <h3 id="schedule-preview-heading" className="text-sm font-semibold text-gray-900 dark:text-gray-100">Next three Europe/London times</h3>
                {preview.length > 0 ? (
                  <ol aria-labelledby="schedule-preview-heading" className="mt-3 space-y-2 text-sm text-gray-700 dark:text-gray-200">
                    {preview.slice(0, 3).map(item => (
                      <li key={item.localSlotKey} className="flex min-w-0 items-center gap-3 rounded-lg border border-gray-200 bg-white px-3 py-3 dark:border-gray-700 dark:bg-gray-800">
                        <span className="flex shrink-0 items-center gap-1.5 text-primary-600 dark:text-primary-300">
                          <CalendarDaysIcon aria-hidden="true" className="h-5 w-5" />
                          <ClockIcon aria-hidden="true" className="h-5 w-5" />
                        </span>
                        <span className="min-w-0 font-medium">{item.local}</span>
                      </li>
                    ))}
                  </ol>
                ) : !previewError ? (
                  <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">Upcoming runs will appear here.</p>
                ) : null}
              </section>

              <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                <button
                  ref={cancelRef}
                  type="button"
                  onClick={onClose}
                  disabled={saving}
                  className="inline-flex items-center justify-center rounded-md bg-gray-200 px-4 py-2 text-sm font-medium text-gray-800 transition-all duration-200 hover:bg-gray-300 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  ref={saveRef}
                  type="button"
                  disabled={Boolean(fieldError) || saving}
                  onClick={() => draft.job.broadConfirmation ? setConfirming(true) : save()}
                  className="inline-flex items-center justify-center rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-all duration-200 hover:bg-primary-700 active:scale-[.98] disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900 motion-reduce:transition-none"
                >
                  {saving ? 'Working...' : 'Save schedule'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ConfirmDialog
        isOpen={confirming}
        title="Confirm broad schedule change"
        message="This schedule can affect several downstream operations. Existing queued work is not changed, and missed windows are not caught up automatically."
        confirmLabel="Save schedule change"
        loading={saving}
        onClose={closeBroadConfirm}
        onConfirm={save}
      />
    </>
  );
};

const SchedulingPage: React.FC = () => {
  const queryClient = useQueryClient();
  const {data, isLoading, error} = useQuery(operationsJobsQuery());
  const update = useMutation(updateJobMutation(queryClient));
  const runNow = useMutation(runNowMutation(queryClient));
  const [editing, setEditing] = useState<EditState | null>(null);
  const [confirmToggle, setConfirmToggle] = useState<{job: ScheduledJob; enabled: boolean; opener: HTMLElement} | null>(null);
  const [confirmRun, setConfirmRun] = useState<{job: ScheduledJob; opener: HTMLElement} | null>(null);
  const [notice, setNotice] = useState('');
  const openerRef = useRef<HTMLElement | null>(null);
  const [runningJob, setRunningJob] = useState('');

  const closeEdit = () => {
    setEditing(null);
    openerRef.current?.focus();
    queueMicrotask(() => openerRef.current?.focus());
    window.setTimeout(() => openerRef.current?.focus(), 0);
  };

  const restoreFocus = (element?: HTMLElement | null) => {
    element?.focus();
    queueMicrotask(() => element?.focus());
    window.setTimeout(() => element?.focus(), 0);
  };

  const openEdit = (job: ScheduledJob, opener: HTMLElement) => {
    openerRef.current = opener;
    setEditing({job, schedule: job.schedule, enabled: job.enabled, version: job.version});
  };

  const saveEdit = async (state: EditState): Promise<void> => {
    await update.mutateAsync({
      jobKey: state.job.key,
      input: {schedule: state.schedule, enabled: state.enabled, expectedVersion: state.version},
    });
  };

  const toggleEnabled = async () => {
    if (!confirmToggle) return;
    const next = confirmToggle;
    setConfirmToggle(null);
    restoreFocus(next.opener);
    await update.mutateAsync({
      jobKey: next.job.key,
      input: {schedule: next.job.schedule, enabled: next.enabled, expectedVersion: next.job.version},
    });
  };

  const runJobNow = async (job: ScheduledJob, opener?: HTMLElement) => {
    setRunningJob(job.key);
    try {
      const result = await runNow.mutateAsync(job.key);
      setNotice(result.created ? `${job.label} manual run queued.` : `Existing active run reused for ${job.label}.`);
    } finally {
      setRunningJob('');
      restoreFocus(opener);
    }
  };

  const closeRunConfirm = () => {
    const opener = confirmRun?.opener;
    setConfirmRun(null);
    restoreFocus(opener);
  };

  const confirmRunNow = () => {
    if (!confirmRun) return;
    const next = confirmRun;
    setConfirmRun(null);
    runJobNow(next.job, next.opener);
  };

  if (isLoading) return <SkeletonCard label="Loading scheduling" />;
  if (error || !data) return <Card><div className="p-6 text-red-600 dark:text-red-400">Failed to load scheduling controls.</div></Card>;

  const scheduledJobs: ScheduledJob[] = data.jobs;
  const workers: WorkerView[] = data.workers;

  return (
    <div className="space-y-6 text-gray-900 dark:text-gray-100">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold">Scheduling</h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">Fixed timezone: Europe/London. Missed windows are not caught up automatically.</p>
        </div>
        <StatusBadge tone={data.scheduler.running ? 'green' : 'red'}>{data.scheduler.running ? 'Scheduler healthy' : 'Scheduler unhealthy'}</StatusBadge>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <Card><div className="text-sm text-gray-500 dark:text-gray-400">Registered jobs</div><div className="mt-1 text-2xl font-semibold">{data.scheduler.registeredJobs} registered jobs</div></Card>
        <Card><div className="text-sm text-gray-500 dark:text-gray-400">Available jobs</div><div className="mt-1 text-2xl font-semibold">{scheduledJobs.filter(job => job.available).length}</div></Card>
        <Card><div className="text-sm text-gray-500 dark:text-gray-400">Paused jobs</div><div className="mt-1 text-2xl font-semibold">{scheduledJobs.filter(job => !job.enabled).length}</div></Card>
      </div>

      {notice && <div role="status" className="rounded-md bg-blue-50 p-3 text-sm text-blue-800 dark:bg-blue-900/20 dark:text-blue-300">{notice}</div>}

      <section>
        <h2 className="mb-3 text-lg font-semibold">Scheduled jobs</h2>
        <div className="grid gap-4 lg:grid-cols-2">
          {scheduledJobs.map(job => {
            const unavailable = !job.available;
            return (
              <article key={job.key} aria-label={`${job.label} schedule card`} className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-900">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-gray-100">{job.label}</h3>
                    {job.description && <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{job.description}</p>}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <StatusBadge tone={unavailable ? 'red' : job.enabled ? 'green' : 'gray'}>{unavailable ? 'Unavailable' : job.enabled ? 'Available' : 'Paused'}</StatusBadge>
                    {hasActiveRun(job) && <StatusBadge tone="amber">Active run</StatusBadge>}
                  </div>
                </div>
                <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
                  <div><dt className="text-gray-500 dark:text-gray-400">Schedule</dt><dd>{describeSchedule(job.schedule)}</dd></div>
                  <div><dt className="text-gray-500 dark:text-gray-400">Last result</dt><dd>{resultText(job)}</dd></div>
                </dl>
                {job.unavailableReason && <p className="mt-3 rounded-md bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300">{job.unavailableReason}</p>}
                <div className="mt-4 flex flex-wrap gap-2">
                  <Button type="button" variant="outline" disabled={unavailable} onClick={event => openEdit(job, event.currentTarget)}>Edit schedule</Button>
                  <Button type="button" variant="secondary" disabled={unavailable} onClick={event => setConfirmToggle({job, enabled: !job.enabled, opener: event.currentTarget})}>{job.enabled ? 'Pause' : 'Resume'}</Button>
                  <Button type="button" disabled={unavailable || runningJob === job.key} loading={runningJob === job.key} onClick={event => job.broadConfirmation ? setConfirmRun({job, opener: event.currentTarget}) : runJobNow(job, event.currentTarget)}>Run now</Button>
                  <a className="inline-flex items-center rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800" href={`/admin/queue?jobKey=${encodeURIComponent(job.key)}`}>View queued runs for {job.label}</a>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <section>
        <h2 className="mb-3 text-lg font-semibold">Workers</h2>
        <div className="grid gap-4 lg:grid-cols-3">
          {workers.map(worker => <WorkerCard key={worker.key} worker={worker} />)}
        </div>
      </section>

      <EditDialog state={editing} saving={update.isPending} onClose={closeEdit} onSave={saveEdit} />
      <ConfirmDialog
        isOpen={Boolean(confirmToggle)}
        title={confirmToggle?.enabled ? 'Resume scheduled job?' : 'Pause scheduled job?'}
        message="Manual runs remain available while a job is paused. Existing queued work is not changed."
        confirmLabel={confirmToggle?.enabled ? 'Resume job' : 'Pause job'}
        loading={update.isPending}
        onClose={() => {
          const opener = confirmToggle?.opener;
          setConfirmToggle(null);
          restoreFocus(opener);
        }}
        onConfirm={toggleEnabled}
      />
      <ConfirmDialog
        isOpen={Boolean(confirmRun)}
        title="Confirm broad manual run"
        message="This manual run can enqueue broad downstream work immediately. Paused scheduling does not block manual runs."
        confirmLabel="Run job now"
        loading={runNow.isPending}
        onClose={closeRunConfirm}
        onConfirm={confirmRunNow}
      />
    </div>
  );
};

export default SchedulingPage;
